import { useMemo, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";
import { FiPlus } from "react-icons/fi";

import { type ATTN, type ENRL } from "@/models.ts";
import { type CHLD } from "@/models/chld";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import { formatAttendanceMonth } from "./useATTNFormLogic";
import ATTNRecordDialog from "./dialogs/ATTNRecordDialog";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalChildId", field: "LocalChildId", title: "Local Child ID (CICN)" },
  { id: "CSPPID", field: "CSPPID", title: "CSPPID" },
  { id: "Month", field: "Month", title: "Month" },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Local Child ID (CICN)", key: "LocalChildId" },
  { label: "CSPPID", key: "CSPPID" },
  { label: "Month", key: "Month" },
];

type ATTNRow = ATTN & {
  PreschoolName?: string;
  ChildName?: string;
  Month: string;
  __raw: ATTN;
};

export default function ATTNRecordsTab({
  attnRecords,
  enrlRecords,
  child,
}: {
  attnRecords: ATTN[];
  enrlRecords: ENRL[];
  child: CHLD;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [popupATTN, setPopupATTN] = useState<ATTN | undefined>();
  const [attnDefaults, setAttnDefaults] = useState<Partial<ATTN> | undefined>();

  const canEdit = leaContext.hasRole("ATTNEdit");

  const closeDialog = () => {
    setDialogOpen(false);
    setPopupATTN(undefined);
    setAttnDefaults(undefined);
  };

  const onAddButtonClick = () => {
    const defaults: Partial<ATTN> = {
      CSPPID: child.CSPPID ?? "",
      LocalChildId: child.LocalChildId ?? "",
      ProgramYear: child.ProgramYear ?? "",
      PreschoolCode: child.PreschoolCode ?? "",
      FirstOfMonth: "",
      DaysExpected: undefined,
      DaysAttended: undefined,
      DaysAbsentExcused: undefined,
      DaysAbsentUnexcused: undefined,
      DaysAbsentSuspension: undefined,
      DaysAttendedSuspension: undefined,
    };
    setAttnDefaults(defaults);
    setPopupATTN(undefined);
    setDialogOpen(true);
  };

  const onRowClick = (record: ATTN) => {
    setAttnDefaults(undefined);
    setPopupATTN(record);
    setDialogOpen(true);
  };

  const childName = [
    child.ChildFirstName,
    child.ChildMiddleName,
    child.ChildLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const tableData: ATTNRow[] = useMemo(() => {
    return (attnRecords ?? [])
      .map((record) => ({
        ...record,
        ChildName: childName,
        PreschoolName: leaContext?.currentOrgPreschools?.find(
          (x) => x.PreschoolCDSCode === record.PreschoolCode
        )?.PreschoolName,
        Month: formatAttendanceMonth(record.FirstOfMonth),
        __raw: record,
      }))
      .sort((a, b) => b.FirstOfMonth.localeCompare(a.FirstOfMonth));
  }, [attnRecords, leaContext?.currentOrgPreschools, childName]);

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export attendance records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(["ChildName"], "attendance-record")}
          />
        </TooltipTrigger>

        {canEdit && (
          <TooltipTrigger tooltipText="Add an attendance record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        initialSort={[{ field: "FirstOfMonth", dir: "desc" }]}
        onRowClick={(event: GridRowClickEvent) =>
          onRowClick((event.dataItem as ATTNRow).__raw)
        }
      />

      {dialogOpen && (
        <ATTNRecordDialog
          selectedATTN={popupATTN}
          attnDefaults={attnDefaults}
          attnRecords={attnRecords}
          enrlRecords={enrlRecords}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
